/**
 * The Equipment step's register columns — Name, Asset Identifier, Product, Plan, BID
 * (`equipment-page-landed`, R16; `equipment-page-table`, R2–R5, R7; frames 04 and 07).
 *
 * One set, shared by the Groups view and the Rows view, so a row reads the same in both. Every
 * state comes from the ONE identify model the step builds; nothing here re-derives a row's state.
 * Sorting and editing in the cell are opt-in (`sortable`, `editable`, NFR3). The Product cell's
 * override marks (R3) and an added row's mark are NOT options: they are part of the cells, so every
 * host — the Groups view and the identify card's narrow table included — shows them.
 *
 * Every colour is a status role's (`semantic.status`, `onboarding-status-colours-equipment`): the
 * state tags read `equipmentTagLook`; *Doesn't fit* is a problem; override marks and the source tag
 * are information; an added row carries the lasting just-changed mark.
 */
import React from 'react';
import styled from 'styled-components';
import { CHANGE_GLYPH, Icon, Tag, type TableColumn } from '@repo/design-system';
import { visuallyHidden } from '@repo/ui';
import { fontMono, ink, semantic, space, typeScale, weight } from '@repo/design-tokens';
import {
  catalogueMatchText,
  IDENTITY_STATE_LABELS,
  PLAN_STATE_LABELS,
  registerRowName,
  type AssetIdentityState,
  type AssetPlanState,
  type IdentifyModel,
  type SortAccessors,
} from '@repo/front-end-utils';
import type { TwinAssetReferenceSourceTag, TwinAssetResponse } from '@repo/front-end-services';

import { equipmentTagLook } from './equipmentFilter';

const { roles } = semantic.status;

/** No rows marked as added: one empty set, so the default never changes the columns' identity. */
const NO_ROWS: ReadonlySet<number> = new Set();

/**
 * The added mark beside a row's name: the added glyph in the lasting just-changed ink, as the
 * launch change lists draw it (`ModelDiffView`, `LaunchRenumbered`). The glyph is decorative; the
 * hidden word says it.
 */
const AddedMark = styled.span`
  display: inline-flex;
  align-items: center;
  flex: none;
  color: ${semantic.status.lastingMark.ink};
`;

/** Two lines in one cell; each clips itself, as the list look asks. */
const Lines = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > .sub {
    color: ${ink[3]};
    font-size: ${typeScale.captionCompact.fontSize};
    line-height: ${typeScale.captionCompact.lineHeight};
  }
  & > .mono {
    font-family: ${fontMono};
  }
  & > .named {
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
    min-width: 0;
  }
  & > .named > span:last-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /*
   * The row the asset card is open on (frame 08): its name in bold. Keyed on the row the table
   * itself marks current (\`activeKey\`), so moving the open row never rebuilds the columns.
   */
  [role='row'][aria-current='true'] & > .named {
    font-weight: ${weight.semibold};
  }
  & > .tag {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${space[1]};
    overflow: visible;
  }
`;

/**
 * One override mark: *3D override* beside the Product cell's state tag (frame 07: the `pen-ruler`
 * glyph at the dense tag's own size, weight 500), in the information ink: a fact about where a
 * value came from, never a job. The words carry the meaning, never the colour alone.
 */
const Mark = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  color: ${roles.information.ink};
  font-size: ${typeScale.caption2.fontSize};
  line-height: ${typeScale.caption2.lineHeight};
  font-weight: ${weight.medium};
  white-space: nowrap;
`;

/** The values a row can override, in the order their marks are joined (R3). */
const OVERRIDE_MARKS = [
  ['capacity_source', 'Capacity override'],
  ['model_3d_source', '3D override'],
] as const;

/**
 * The marks for `row`: one per value whose OWN source is `'override'` — never inferred from the
 * `catalogue_*` values, so a catalogue that has not been read cannot change them (R3).
 */
export const overrideMarks = (row: TwinAssetResponse): string[] =>
  OVERRIDE_MARKS.filter(([field]) => row[field] === 'override').map(([, label]) => label);

/**
 * Where the row's product came from (`equipment-page-adoption`, R10; frame 09's source tag, built as
 * card 05's code chip). The server serves the tag only while the row still holds that product.
 */
const SOURCE_TAGS: Record<TwinAssetReferenceSourceTag, { text: string; label: string }> = {
  bi: { text: 'BI', label: 'Product from BI' },
  file: { text: 'File', label: 'Product from the adoption file' },
  lookup: { text: 'Lookup', label: 'Product found by lookup' },
};

export const sourceTagOf = (row: TwinAssetResponse) => {
  const tag = row.reference_source_tag;
  return tag ? (SOURCE_TAGS[tag] ?? null) : null;
};

/**
 * True for a Brand or Generic row the fit judge flagged and no person has kept
 * (`equipment-page-adoption`, R9). An unjudged row (`doesnt_fit` null or absent) is never flagged.
 */
export const showsDoesntFit = (
  row: TwinAssetResponse,
  state: AssetIdentityState | null | undefined,
): boolean =>
  (state === 'brand' || state === 'generic') &&
  row.doesnt_fit === true &&
  row.doesnt_fit_kept !== true;

/** The *Doesn't fit* tag's accessible name: the kind the row's name says, when the server names it. */
export const doesntFitLabel = (row: TwinAssetResponse): string => {
  const kind = row.doesnt_fit_detail?.nameKind?.trim();
  return kind ? `Doesn’t fit: the name says ${kind}` : 'Doesn’t fit';
};

/** The customer's own name for the row and its category, beneath the display name. */
const nameLine = (row: TwinAssetResponse): string | null => {
  const display = registerRowName(row);
  const parts = [row.name !== display ? row.name : null, row.category_raw?.trim() || null].filter(
    (part): part is string => Boolean(part),
  );
  return parts.length > 0 ? parts.join(' · ') : null;
};

const NOT_A_PRODUCT = 'Not a catalogue product';
const NO_GUESS = 'No guess yet';

/** What the Product cell names: the match, else the guess, else what the state means. */
const productText = (row: TwinAssetResponse, model: Pick<IdentifyModel, 'stateOf'>): string => {
  const matched = catalogueMatchText(row);
  if (matched) return matched;
  const state = model.stateOf(row.id);
  if (state === 'to_confirm' && row.match_proposal) {
    return [row.match_proposal.manufacturer, row.match_proposal.product]
      .filter(Boolean)
      .join(' · ');
  }
  if (state === 'not_tracked') return NOT_A_PRODUCT;
  return NO_GUESS;
};

/** The Plan column's order: on the plan first, an unknown plan last (R5). */
const PLAN_RANK: Record<AssetPlanState, number> = {
  on_plan: 0,
  not_on_plan: 1,
  keep_off_plan: 2,
  plan_not_loaded: 3,
};

/**
 * What each column sorts on (R5), for `sortRows`: a row's own text, case-insensitive and
 * number-aware there, with `null` — no value — last in both directions.
 */
export const equipmentSortAccessors = (
  model: Pick<IdentifyModel, 'stateOf' | 'planStateOf'>,
): SortAccessors<TwinAssetResponse> => ({
  name: row => registerRowName(row),
  identifier: row => row.customer_asset_identifier?.trim() || null,
  // The cell's own text, so a guess sorts by its guessed product; a cell with no product is null.
  product: row => {
    const text = productText(row, model);
    return text === NOT_A_PRODUCT || text === NO_GUESS || !text ? null : text;
  },
  plan: row => {
    const plan = model.planStateOf(row.id);
    return plan ? PLAN_RANK[plan] : null;
  },
  bid: row => row.entity_code || null,
});

/** The two values edited in the cell (R7). */
export type EditableRegisterField = 'name' | 'identifier';

export interface EquipmentRegisterColumnOptions {
  /** frame 06: the docked identify card narrows the table to Name, Product and Plan. */
  narrow?: boolean;
  /** Every column's header sorts (the Rows view, R5). The host orders the rows. */
  sortable?: boolean;
  /**
   * Name and Asset Identifier are edited in the cell (R7): `editCell` renders the value's line,
   * which the host draws as the cell's button and, while it is open, its field.
   */
  editable?: boolean;
  /**
   * `sub` is the line the cell draws beneath its value (what the customer wrote; the source id).
   * The cell draws it, because while it is open the answer line takes its place: the row keeps
   * frame 07's height.
   */
  editCell?: (
    row: TwinAssetResponse,
    field: EditableRegisterField,
    sub: string | null,
  ) => React.ReactNode;
  /**
   * Rows to mark as added: each one's name carries the lasting just-changed mark, the added glyph
   * with the hidden word *added* (`onboarding-status-colours-equipment`, R6). The Rows view passes
   * the row the add row just made; the equipment page's re-import will pass the rows it added.
   * Absent or empty marks nothing.
   */
  addedIds?: ReadonlySet<number>;
  /**
   * Frame 08's mark beside the open row's name (`equipment-page-asset-card`, R2), drawn by the host
   * for every row — it renders nothing except on the open one. A STABLE function, so the open row
   * moving re-renders the mark alone, never the columns (the table memoises its rows on them).
   */
  openMark?: (rowId: number) => React.ReactNode;
}

/**
 * The columns frame 06 keeps while the identify card is docked beside the list: *The table narrows
 * to Name, Product and Plan* (`equipment-page-identify`, R3).
 */
const NARROW_KEYS: ReadonlySet<string> = new Set(['name', 'product', 'plan']);

export const equipmentRegisterColumns = (
  model: Pick<IdentifyModel, 'stateOf' | 'planStateOf'>,
  {
    narrow = false,
    sortable = false,
    editable = false,
    editCell,
    addedIds = NO_ROWS,
    openMark,
  }: EquipmentRegisterColumnOptions = {},
): TableColumn<TwinAssetResponse>[] => {
  const edit = editable ? editCell : undefined;
  const all: TableColumn<TwinAssetResponse>[] = [
    {
      key: 'name',
      header: 'Name',
      width: 'minmax(0, 1.25fr)',
      render: row => {
        const sub = nameLine(row);
        return (
          <Lines>
            <span className="named">
              {openMark?.(row.id)}
              {addedIds.has(row.id) && (
                <AddedMark>
                  <Icon name={CHANGE_GLYPH.added} size="xs" />
                  <span style={visuallyHidden}>added</span>
                </AddedMark>
              )}
              {edit ? edit(row, 'name', sub) : <span>{registerRowName(row)}</span>}
            </span>
            {!edit && sub && <span className="sub">{sub}</span>}
          </Lines>
        );
      },
    },
    {
      key: 'identifier',
      header: 'Asset Identifier',
      width: '128px',
      render: row => (
        <Lines>
          {edit ? (
            edit(row, 'identifier', row.source_asset_id || null)
          ) : (
            <>
              <span className="mono">{row.customer_asset_identifier || '—'}</span>
              {row.source_asset_id && <span className="mono sub">{row.source_asset_id}</span>}
            </>
          )}
        </Lines>
      ),
    },
    {
      key: 'product',
      header: 'Product',
      width: 'minmax(0, 1.5fr)',
      render: row => {
        const state = model.stateOf(row.id);
        const marks = overrideMarks(row);
        const source = sourceTagOf(row);
        const doesntFit = showsDoesntFit(row, state);
        return (
          <Lines>
            <span>{productText(row, model)}</span>
            {(state || marks.length > 0 || source || doesntFit) && (
              <span className="tag">
                {/* Frame 09 draws *Doesn't fit* in the state tag's place: the row is Brand or
                    Generic, but the flag is what it asks of a person (`equipment-page-adoption`). */}
                {state && !doesntFit && (
                  <Tag surface="on-glass" size="dense" {...equipmentTagLook(state)}>
                    {IDENTITY_STATE_LABELS[state]}
                  </Tag>
                )}
                {doesntFit && (
                  <Tag
                    surface="on-glass"
                    size="dense"
                    tone={roles.problem.tagTone}
                    icon={<Icon name="triangle-exclamation" size="xs" />}
                    aria-label={doesntFitLabel(row)}
                  >
                    Doesn’t fit
                  </Tag>
                )}
                {marks.map(mark => (
                  <Mark key={mark}>
                    <Icon name="pen-ruler" size="xs" />
                    {mark}
                  </Mark>
                ))}
                {source && (
                  <Tag
                    surface="on-glass"
                    tone={roles.information.tagTone}
                    aria-label={source.label}
                  >
                    {source.text}
                  </Tag>
                )}
              </span>
            )}
          </Lines>
        );
      },
    },
    {
      key: 'plan',
      header: 'Plan',
      width: '116px',
      render: row => {
        const plan = model.planStateOf(row.id);
        return plan ? (
          <Tag surface="on-glass" size="dense" {...equipmentTagLook(plan)}>
            {PLAN_STATE_LABELS[plan]}
          </Tag>
        ) : null;
      },
    },
    {
      key: 'bid',
      header: 'BID',
      width: '112px',
      render: row => (
        <Lines>
          <span className={row.entity_code ? 'mono' : 'mono sub'}>
            {row.entity_code || 'no BID yet'}
          </span>
        </Lines>
      ),
    },
  ];
  const shown = narrow ? all.filter(column => NARROW_KEYS.has(String(column.key))) : all;
  // No render here reads its row index, so a re-sort need not re-render the cells (NFR1).
  return shown.map(column => ({
    ...column,
    readsIndex: false,
    ...(sortable ? { sortable: true } : {}),
  }));
};
